// src/lib/face_features.ts

import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import type { NormalizedLandmark } from '@mediapipe/tasks-vision';
import { FACE_CONFIG } from '../config/constants';

let faceLandmarker: FaceLandmarker | null = null;

export async function initializeFaceLandmarker(): Promise<FaceLandmarker> {
  if (faceLandmarker) {
    return faceLandmarker;
  }

  const vision = await FilesetResolver.forVisionTasks(
    "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
  );

  faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
    baseOptions: {
      modelAssetPath: FACE_CONFIG.MODEL_PATH,
      delegate: "GPU",
    },
    runningMode: "VIDEO",
    numFaces: 1,
    minFaceDetectionConfidence: FACE_CONFIG.MIN_DETECTION_CONFIDENCE,
  });

  return faceLandmarker;
}

export function disposeFaceLandmarker(): void {
  if (faceLandmarker) {
    faceLandmarker.close();
    faceLandmarker = null;
  }
}

function distance(p1: NormalizedLandmark, p2: NormalizedLandmark): number {
  const dx = p1.x - p2.x;
  const dy = p1.y - p2.y;
  return Math.sqrt(dx * dx + dy * dy);
}

export function computeEyeOpenRatio(landmarks: NormalizedLandmark[]): number {
  // Left eye vertical distances
  const left_v1 = distance(landmarks[159], landmarks[145]);
  const left_v2 = distance(landmarks[160], landmarks[144]);
  // Left eye horizontal distance
  const left_h = distance(landmarks[33], landmarks[133]);
  const left_ear = (left_v1 + left_v2) / (2 * left_h);

  // Right eye vertical distances
  const right_v1 = distance(landmarks[374], landmarks[387]);
  const right_v2 = distance(landmarks[385], landmarks[380]);
  // Right eye horizontal distance
  const right_h = distance(landmarks[263], landmarks[362]);
  const right_ear = (right_v1 + right_v2) / (2 * right_h);

  // Average and normalize to 0-1
  const avgEar = (left_ear + right_ear) / 2;
  return Math.min(1, avgEar / 0.3);
}

export function computePainScore(landmarks: NormalizedLandmark[]): number {
  // Simplified pain detection based on brow position
  // Brow lowering: distance between brow and eye
  const leftBrowEyeDist = distance(landmarks[55], landmarks[159]);
  const rightBrowEyeDist = distance(landmarks[285], landmarks[386]);
  const avgBrowEyeDist = (leftBrowEyeDist + rightBrowEyeDist) / 2;

  // Eye squinting (use eye open ratio)
  const eyeOpen = computeEyeOpenRatio(landmarks);

  // Combine features
  const painFeature = (1 - avgBrowEyeDist * 20) + (1 - eyeOpen) * 0.5;

  // Map to 0-3 scale
  const thresholds = FACE_CONFIG.PAIN_THRESHOLDS;
  if (painFeature < thresholds[0]) return 0;
  if (painFeature < thresholds[1]) return 1;
  if (painFeature < thresholds[2]) return 2;
  return 3;
}

export function aggregateFaceFeatures(
  eyeOpenRatios: number[],
  painScores: number[]
): { eye_open_ratio: number | null; pain_face_score: number | null } {
  if (eyeOpenRatios.length === 0 || painScores.length === 0) {
    return { eye_open_ratio: null, pain_face_score: null };
  }

  // Average eye open ratio
  const avgEyeOpen = eyeOpenRatios.reduce((a, b) => a + b, 0) / eyeOpenRatios.length;

  // Max pain score (most severe expression)
  const maxPain = Math.max(...painScores);

  return {
    eye_open_ratio: avgEyeOpen,
    pain_face_score: Math.round(maxPain),
  };
}
